import { useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import {
  trackEvent,
  trackButtonClick,
  resetDurationTracking,
  getAnonymousId,
} from '@/lib/analytics';

/**
 * Hook for tracking page views and duration on route changes
 */
export function usePageTracking(): void {
  const location = useLocation();

  useEffect(() => {
    const anonymousId = getAnonymousId();
    if (!anonymousId) return;

    // Track page view
    trackEvent('page_view', 'page_viewed', {
      metadata: {
        page_path: location.pathname,
        search: location.search,
      },
    });

    // Reset duration tracking for new page
    resetDurationTracking(location.pathname);
  }, [location.pathname]);
}

/**
 * Hook for tracking button clicks
 * Returns a click handler wrapper
 */
export function useTrackClick() {
  const track = useCallback(
    (
      buttonId: string,
      buttonText?: string,
      metadata?: Record<string, unknown>
    ) => {
      trackButtonClick(buttonId, buttonText, metadata);
    },
    []
  );

  /**
   * Wrap an existing onClick handler with tracking
   */
  const withTracking = useCallback(
    <T extends React.MouseEvent>(
      buttonId: string,
      onClick?: (e: T) => void,
      buttonText?: string,
      metadata?: Record<string, unknown>
    ) => {
      return (e: T) => {
        trackButtonClick(buttonId, buttonText, metadata);
        onClick?.(e);
      };
    },
    []
  );

  return { track, withTracking };
}

/**
 * Hook for tracking custom events
 */
export function useTrackEvent() {
  return useCallback(
    (
      category: string,
      name: string,
      params?: {
        user_id?: string;
        element_id?: string;
        duration_ms?: number;
        metadata?: Record<string, unknown>;
      }
    ) => {
      trackEvent(category, name, params);
    },
    []
  );
}
